import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import type {
  BoxFormatMapping,
  Shipment,
  ShipmentBox,
  ShipmentTrackingEvent,
  ShipmentTrackingEventRevision,
  ShipmentTrackingSummary,
} from 'common/src/utils/types';
import {
  getShipmentForId,
  getShipmentForReference,
  getShipmentBoxesForShipment,
  getShipmentTrackingSummaries,
  getShipmentTrackingEvents,
  getShipmentTrackingEventRevisions,
  refreshShipmentTrackingJourney,
  updateShipmentHeader,
  discoverShipmentTrackingAfterEdit,
  type ShipmentHeaderUpdate,
  updateShipmentDivision,
} from 'common/src/api/shipments';
import { getFruticBoxToFormatMapping } from 'common/src/api/frutic';
import { setReceiverMappingForShipments } from 'common/src/api/exporterReceivers';
import { VALID_FORMATS } from 'common/src/utils/definitions';
import { useTranslation } from 'common/src/hooks/useTranslation';
import {
  buildShipmentHeaderForm,
  emptyShipmentHeaderForm,
  getChangedTrackingIdentityFields,
  shipmentHeaderFields,
  type ShipmentDetailsTab,
  type ShipmentHeaderForm,
} from '../utils/shipment-header-form';
import { useConfirm } from '../components/ui';

interface Options {
  scope: string | null;
  exporterId?: number;
  season?: string;
  speciesId?: number;
  usesFrutic: boolean;
  onHeaderSaved?: (
    shipment: Shipment,
    journeys: ShipmentTrackingSummary[] | null
  ) => void;
  onTrackingUpdated?: (
    shipmentId: number,
    journeys: ShipmentTrackingSummary[]
  ) => void;
  onFormatMapped?: (mapping: BoxFormatMapping) => void;
  onDivisionChanged?: (updated: Shipment) => void;
  onReceiverMapped?: (updated: Shipment) => void;
}
const normalize = (value?: string | null) => (value || '').trim().toUpperCase();

/** The same scoped loader and actions power the ficha in Despachos and Arribos. */
export function useShipmentDetails(options: Options) {
  const { scope, exporterId, season, speciesId, usesFrutic } = options;
  const { t } = useTranslation('shipments');
  const callbacks = useRef(options);
  callbacks.current = options;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const mounted = useRef(false);
  const detailsRequestVersion = useRef(0);
  const savingRequest = useRef<object | null>(null);
  const refreshingRequests = useRef(new Map<string, object>());
  const lastOpen = useRef<{
    reference: string;
    tab: ShipmentDetailsTab;
    journeys: ShipmentTrackingSummary[];
    id?: number;
  } | null>(null);
  const [openScope, setOpenScope] = useState<string | null>(null);
  const confirm = useConfirm();
  const [selectedReferenceNumber, setSelectedReferenceNumber] = useState('');
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(
    null
  );
  const [shipmentHeaderForm, setShipmentHeaderForm] =
    useState<ShipmentHeaderForm>(emptyShipmentHeaderForm);
  const [selectedShipmentBoxes, setSelectedShipmentBoxes] = useState<
    ShipmentBox[]
  >([]);
  const [selectedTrackingJourneys, setSelectedTrackingJourneys] = useState<
    ShipmentTrackingSummary[]
  >([]);
  const [selectedTrackingEvents, setSelectedTrackingEvents] = useState<
    ShipmentTrackingEvent[]
  >([]);
  const [selectedTrackingEventRevisions, setSelectedTrackingEventRevisions] =
    useState<ShipmentTrackingEventRevision[]>([]);
  const [refreshingTrackingJourneyIds, setRefreshingTrackingJourneyIds] =
    useState<Set<string>>(() => new Set());
  const [selectedDetailsTab, setSelectedDetailsTab] =
    useState<ShipmentDetailsTab>('general');
  const [isDetailsLoading, setIsDetailsLoading] = useState(false);
  const [isSavingShipmentHeader, setIsSavingShipmentHeader] = useState(false);
  const [isChangingDivision, setIsChangingDivision] = useState(false);
  const [isMappingReceiver, setIsMappingReceiver] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const invalidateDetails = useCallback(() => {
    detailsRequestVersion.current++;
    savingRequest.current = null;
    refreshingRequests.current.clear();
    lastOpen.current = null;
  }, []);

  useEffect(() => {
    mounted.current = true;
    setOpenScope(null);
    setIsDetailsLoading(false);
    setIsSavingShipmentHeader(false);
    setRefreshingTrackingJourneyIds(new Set());
    return () => {
      mounted.current = false;
      invalidateDetails();
    };
  }, [scope, invalidateDetails]);

  const handleCloseDetails = useCallback(() => {
    if (savingRequest.current) return;
    detailsRequestVersion.current++;
    refreshingRequests.current.clear();
    lastOpen.current = null;
    setRefreshingTrackingJourneyIds(new Set());
    setIsDetailsLoading(false);
    setOpenScope(null);
  }, []);

  const handleViewDetails = useCallback(
    async (
      referenceNumber: string,
      initialTab: ShipmentDetailsTab = 'general',
      trackingJourneys: ShipmentTrackingSummary[] = [],
      shipmentId?: number
    ) => {
      if (
        !scope ||
        !exporterId ||
        !season ||
        !speciesId ||
        savingRequest.current
      )
        return;
      const requestVersion = ++detailsRequestVersion.current;
      const isCurrent = () =>
        mounted.current &&
        scopeRef.current === scope &&
        requestVersion === detailsRequestVersion.current;
      lastOpen.current = {
        reference: referenceNumber,
        tab: initialTab,
        journeys: trackingJourneys,
        id: shipmentId,
      };
      refreshingRequests.current.clear();
      setRefreshingTrackingJourneyIds(new Set());
      setOpenScope(scope);
      setSelectedReferenceNumber(referenceNumber);
      setSelectedDetailsTab(initialTab);
      setSelectedShipment(null);
      setShipmentHeaderForm(emptyShipmentHeaderForm);
      setSelectedShipmentBoxes([]);
      setSelectedTrackingJourneys(trackingJourneys);
      setSelectedTrackingEvents([]);
      setSelectedTrackingEventRevisions([]);
      setLoadError(null);
      setIsDetailsLoading(true);
      try {
        const shipment = shipmentId
          ? await getShipmentForId(exporterId, season, speciesId, shipmentId)
          : await getShipmentForReference(
              exporterId,
              season,
              speciesId,
              referenceNumber
            );
        if (!isCurrent()) return;
        if (
          !shipment ||
          shipment.exporter_id !== exporterId ||
          shipment.season !== season ||
          shipment.species_id !== speciesId
        )
          throw new Error('Shipment missing or outside current scope');
        const [data, summaries, formatMappings] = await Promise.all([
          getShipmentBoxesForShipment(shipment),
          getShipmentTrackingSummaries(exporterId, season, speciesId, [
            shipment.reference_number,
          ]),
          getFruticBoxToFormatMapping({ exporter_id: exporterId }),
        ]);
        if (!isCurrent()) return;
        if (!summaries) throw new Error('Tracking summary unavailable');
        const journeys = summaries[0]?.tracking_journeys || [];
        const ids = journeys.map((journey) => journey.id);
        const [events, revisions] = await Promise.all([
          getShipmentTrackingEvents(ids),
          getShipmentTrackingEventRevisions(ids),
        ]);
        if (!isCurrent()) return;
        if (!events || !revisions)
          throw new Error('Tracking history unavailable');
        if (!formatMappings) throw new Error('Format mappings unavailable');
        const mappings = formatMappings as BoxFormatMapping[];
        const mappedBoxes = data.map((box) => {
          const mapping = mappings.find(
            (m) =>
              m.exporter_id === exporterId &&
              normalize(m.frutic_code) === normalize(box.box_type)
          );
          const format = normalize(box.box_format);
          return {
            ...box,
            box_format:
              mapping?.cherry_box_format ||
              (VALID_FORMATS.includes(format) ? format : 'NO ENCONTRADO'),
          };
        });
        setSelectedShipment(shipment);
        setSelectedReferenceNumber(
          shipment.reference_number || referenceNumber
        );
        setShipmentHeaderForm(buildShipmentHeaderForm(shipment));
        setSelectedShipmentBoxes(mappedBoxes);
        setSelectedTrackingJourneys(journeys);
        setSelectedTrackingEvents(events);
        setSelectedTrackingEventRevisions(revisions);
      } catch {
        if (isCurrent())
          setLoadError(t('No se pudieron cargar los datos del despacho.'));
      } finally {
        if (isCurrent()) setIsDetailsLoading(false);
      }
    },
    [scope, exporterId, season, speciesId, t]
  );

  const handleRefreshTrackingJourney = useCallback(
    async (trackingJourneyId: string) => {
      if (
        !selectedShipment ||
        !scope ||
        savingRequest.current ||
        refreshingRequests.current.size > 0 ||
        !selectedTrackingJourneys.some((j) => j.id === trackingJourneyId)
      )
        return;
      const version = detailsRequestVersion.current;
      const token = {};
      refreshingRequests.current.set(trackingJourneyId, token);
      const isCurrent = () =>
        mounted.current &&
        scopeRef.current === scope &&
        version === detailsRequestVersion.current &&
        refreshingRequests.current.get(trackingJourneyId) === token;
      setRefreshingTrackingJourneyIds(
        (current) => new Set([...current, trackingJourneyId])
      );
      try {
        const result = await refreshShipmentTrackingJourney(trackingJourneyId);
        if (!isCurrent()) return;
        if (result.synced === 0) {
          toast.info(
            t(
              'El seguimiento ya está actualizado. Intenta nuevamente en unos minutos.'
            )
          );
          return;
        }
        const summaries = await getShipmentTrackingSummaries(
          selectedShipment.exporter_id,
          selectedShipment.season,
          selectedShipment.species_id,
          [selectedShipment.reference_number]
        );
        if (!isCurrent()) return;
        if (!summaries) throw new Error('Tracking summary unavailable');
        const journeys = summaries[0]?.tracking_journeys || [];
        const ids = journeys.map((j) => j.id);
        const [events, revisions] = await Promise.all([
          getShipmentTrackingEvents(ids),
          getShipmentTrackingEventRevisions(ids),
        ]);
        if (!isCurrent()) return;
        if (!events || !revisions)
          throw new Error('Tracking history unavailable');
        setSelectedTrackingJourneys(journeys);
        setSelectedTrackingEvents(events);
        setSelectedTrackingEventRevisions(revisions);
        callbacks.current.onTrackingUpdated?.(selectedShipment.id, journeys);
        toast.success(t('Seguimiento actualizado correctamente'));
      } catch {
        if (isCurrent()) toast.error(t('No se pudo actualizar el seguimiento'));
      } finally {
        if (isCurrent()) {
          refreshingRequests.current.delete(trackingJourneyId);
          setRefreshingTrackingJourneyIds((current) => {
            const next = new Set(current);
            next.delete(trackingJourneyId);
            return next;
          });
        }
      }
    },
    [selectedShipment, selectedTrackingJourneys, scope, t]
  );

  const handleShipmentHeaderChange = useCallback(
    (key: keyof ShipmentHeaderUpdate, value: string) => {
      if (!savingRequest.current)
        setShipmentHeaderForm((current) => ({ ...current, [key]: value }));
    },
    []
  );

  const handlePriceFormatMapped = useCallback(
    (mapping: BoxFormatMapping) => {
      if (
        !mounted.current ||
        scopeRef.current !== scope ||
        mapping.exporter_id !== exporterId
      )
        return;
      setSelectedShipmentBoxes((current) =>
        current.map((box) =>
          normalize(box.box_type) === normalize(mapping.frutic_code)
            ? { ...box, box_format: mapping.cherry_box_format }
            : box
        )
      );
      callbacks.current.onFormatMapped?.(mapping);
    },
    [exporterId, scope]
  );

  const handleSaveShipmentHeader = useCallback(async () => {
    if (
      !selectedShipment ||
      !scope ||
      usesFrutic ||
      savingRequest.current ||
      isDetailsLoading ||
      refreshingRequests.current.size > 0 ||
      loadError
    )
      return;
    const updates = Object.fromEntries(
      shipmentHeaderFields.map((field) => [
        field.key,
        shipmentHeaderForm[field.key] || null,
      ])
    ) as ShipmentHeaderUpdate;
    const changedTrackingFields = getChangedTrackingIdentityFields(
      selectedShipment,
      shipmentHeaderForm
    );
    const creditSensitive = changedTrackingFields.filter(
      (field) => field.key === 'container_number' || field.key === 'awb_number'
    );
    if (
      creditSensitive.length &&
      selectedTrackingJourneys.length &&
      !(await confirm({
        title: t('¿Guardar con seguimiento activo?'),
        message: t(
          'Cambiaste {{fields}} en un despacho con seguimiento activo. El journey actual no se modificará: se desvinculará si deja de ser compatible y el nuevo identificador se evaluará por separado. Esto puede consumir otro crédito ShipsGo. ¿Deseas continuar?',
          { fields: creditSensitive.map((field) => t(field.label)).join(', ') }
        ),
        confirmLabel: t('Continuar'),
      }))
    )
      return;
    if (normalize(updates.receiver) !== normalize(selectedShipment.receiver))
      updates.exporter_receiver_id = null;
    const token = {};
    const version = detailsRequestVersion.current;
    savingRequest.current = token;
    const isCurrent = () =>
      mounted.current &&
      scopeRef.current === scope &&
      version === detailsRequestVersion.current &&
      savingRequest.current === token;
    setIsSavingShipmentHeader(true);
    try {
      const updated = await updateShipmentHeader(selectedShipment.id, updates);
      if (!isCurrent()) return;
      let journeys: ShipmentTrackingSummary[] | null = null;
      let warning: string | null = null;
      if (changedTrackingFields.length) {
        try {
          const discovery = await discoverShipmentTrackingAfterEdit(updated.id);
          if (!isCurrent()) return;
          if (!discovery.counts.links_reconciled)
            warning = t(
              'El despacho fue actualizado, pero el nuevo identificador o sus fechas todavía no son elegibles para seguimiento.'
            );
        } catch {
          if (!isCurrent()) return;
          warning = t(
            'El despacho fue actualizado. El seguimiento no pudo reconciliarse ahora y volverá a intentarse en el próximo proceso automático.'
          );
        }
        const summaries = await getShipmentTrackingSummaries(
          updated.exporter_id,
          updated.season,
          updated.species_id,
          [updated.reference_number]
        );
        if (!isCurrent()) return;
        if (summaries) {
          journeys = summaries[0]?.tracking_journeys || [];
          const ids = journeys.map((j) => j.id);
          const [events, revisions] = await Promise.all([
            getShipmentTrackingEvents(ids),
            getShipmentTrackingEventRevisions(ids),
          ]);
          if (!isCurrent()) return;
          setSelectedTrackingJourneys(journeys);
          setSelectedTrackingEvents(events || []);
          setSelectedTrackingEventRevisions(revisions || []);
          if (!events || !revisions)
            warning = t(
              'El despacho y su seguimiento fueron reconciliados, pero no se pudo refrescar el detalle en pantalla.'
            );
        } else
          warning ||= t(
            'El despacho y su seguimiento fueron reconciliados, pero no se pudo refrescar el detalle en pantalla.'
          );
      }
      setSelectedShipment(updated);
      setSelectedReferenceNumber(updated.reference_number || '');
      setShipmentHeaderForm(buildShipmentHeaderForm(updated));
      setSelectedShipmentBoxes(
        (current) =>
          current.map((box) => ({
            ...box,
            ...updates,
            ...Object.fromEntries(
              shipmentHeaderFields.map((field) => [
                field.key,
                updated[field.key] ?? '',
              ])
            ),
            shipment_id: updated.id,
            exporter_receiver_id: updated.exporter_receiver_id ?? null,
          })) as ShipmentBox[]
      );
      callbacks.current.onHeaderSaved?.(updated, journeys);
      toast.success(t('Despacho actualizado correctamente'));
      if (warning) toast.warning(warning);
    } catch {
      if (isCurrent()) toast.error(t('Error al actualizar el despacho'));
    } finally {
      if (isCurrent()) {
        savingRequest.current = null;
        setIsSavingShipmentHeader(false);
      }
    }
  }, [
    selectedShipment,
    shipmentHeaderForm,
    selectedTrackingJourneys.length,
    scope,
    usesFrutic,
    isDetailsLoading,
    loadError,
    t,
  ]);

  // Moves the open shipment to another division. Errors are thrown so the
  // sheet can show them next to the selector.
  const handleChangeDivision = useCallback(
    async (divisionId: number) => {
      if (
        !selectedShipment ||
        !scope ||
        usesFrutic ||
        savingRequest.current ||
        isDetailsLoading ||
        loadError ||
        divisionId === selectedShipment.division_id
      )
        return;
      const token = {};
      const version = detailsRequestVersion.current;
      savingRequest.current = token;
      const isCurrent = () =>
        mounted.current &&
        scopeRef.current === scope &&
        version === detailsRequestVersion.current &&
        savingRequest.current === token;
      setIsChangingDivision(true);
      try {
        const updated = await updateShipmentDivision(
          selectedShipment.id,
          divisionId
        );
        if (!isCurrent()) return;
        setSelectedShipment(updated);
        setSelectedShipmentBoxes((current) =>
          current.map((box) => ({ ...box, division_id: updated.division_id }))
        );
        callbacks.current.onDivisionChanged?.(updated);
        toast.success(t('División del despacho actualizada'));
      } finally {
        if (isCurrent()) {
          savingRequest.current = null;
          setIsChangingDivision(false);
        }
      }
    },
    [selectedShipment, scope, usesFrutic, isDetailsLoading, loadError, t]
  );

  // Maps the open shipment's receiver name to an official receiver. The RPC
  // applies it to every shipment with that name in the season and species.
  const handleMapReceiver = useCallback(
    async (exporterReceiverId: number) => {
      const receiverName = (selectedShipment?.receiver || '').trim();
      if (
        !selectedShipment ||
        !scope ||
        !exporterId ||
        !season ||
        !speciesId ||
        !receiverName ||
        savingRequest.current ||
        isDetailsLoading ||
        loadError
      )
        return;
      const token = {};
      const version = detailsRequestVersion.current;
      savingRequest.current = token;
      const isCurrent = () =>
        mounted.current &&
        scopeRef.current === scope &&
        version === detailsRequestVersion.current &&
        savingRequest.current === token;
      setIsMappingReceiver(true);
      try {
        await setReceiverMappingForShipments({
          exporterId,
          speciesId,
          season,
          receiverName,
          exporterReceiverId,
        });
        if (!isCurrent()) return;
        const updated = {
          ...selectedShipment,
          exporter_receiver_id: exporterReceiverId,
        };
        setSelectedShipment(updated);
        setSelectedShipmentBoxes((current) =>
          current.map((box) => ({
            ...box,
            exporter_receiver_id: exporterReceiverId,
          }))
        );
        callbacks.current.onReceiverMapped?.(updated);
        toast.success(t('Cliente mapeado correctamente'));
      } finally {
        if (isCurrent()) {
          savingRequest.current = null;
          setIsMappingReceiver(false);
        }
      }
    },
    [
      selectedShipment,
      scope,
      exporterId,
      season,
      speciesId,
      isDetailsLoading,
      loadError,
      t,
    ]
  );

  const handleRetryDetails = () => {
    const target = lastOpen.current;
    if (target)
      void handleViewDetails(
        target.reference,
        target.tab,
        target.journeys,
        target.id
      );
  };
  return {
    handleViewDetails,
    handleCloseDetails,
    modalProps: {
      isOpen: !!scope && openScope === scope,
      onClose: handleCloseDetails,
      referenceNumber: selectedReferenceNumber,
      shipment: selectedShipment,
      boxes: selectedShipmentBoxes,
      headerForm: shipmentHeaderForm,
      trackingJourneys: selectedTrackingJourneys,
      trackingEvents: selectedTrackingEvents,
      trackingEventRevisions: selectedTrackingEventRevisions,
      refreshingTrackingJourneyIds,
      selectedTab: selectedDetailsTab,
      isLoading: isDetailsLoading,
      isSaving: isSavingShipmentHeader,
      usesFrutic,
      onTabChange: setSelectedDetailsTab,
      onHeaderChange: handleShipmentHeaderChange,
      onSave: handleSaveShipmentHeader,
      onDivisionChange: handleChangeDivision,
      isChangingDivision,
      onMapReceiver: handleMapReceiver,
      isMappingReceiver,
      onRefreshTrackingJourney: handleRefreshTrackingJourney,
      onFormatMapped: handlePriceFormatMapped,
      loadError,
      onRetry: handleRetryDetails,
    },
  };
}
